// components/TimeEntryCorrections.js
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Alert,
    CircularProgress,
    IconButton,
    Chip
} from "@mui/material";
import {
    Edit as EditIcon,
    AccessTime as AccessTimeIcon,
    History as HistoryIcon
} from "@mui/icons-material";
import {
    collection,
    query,
    orderBy,
    getDocs,
    doc,
    getDoc,
    updateDoc,
    Timestamp
} from "firebase/firestore";
import { db } from "../firebase";

const TimeEntryCorrections = ({ orgId }) => {
    const [timeLogs, setTimeLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [newTimestampString, setNewTimestampString] = useState("");
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [correctionHistory, setCorrectionHistory] = useState([]);

    useEffect(() => {
        fetchTimeLogs();
    }, []);

    const fetchTimeLogs = async () => {
        setLoading(true);
        try {
            const logsRef = collection(db, "organizations", orgId, "timeLogs");
            const q = query(logsRef, orderBy("timestamp", "desc"));
            const snapshot = await getDocs(q);

            const logs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setTimeLogs(logs);
        } catch (err) {
            console.error("Error fetching time logs:", err.message);
            setError("Failed to load time logs. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenEditDialog = (log) => {
        setSelectedLog(log);
        // Set current timestamp as default in format: "YYYY-MM-DDThh:mm"
        const date = log.timestamp.toDate();
        const dateString = date.toISOString().substring(0, 16);
        setNewTimestampString(dateString);
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setNewTimestampString("");
        setSelectedLog(null);
    };

    const handleOpenHistoryDialog = async (log) => {
        try {
            setLoading(true);
            const logRef = doc(db, "organizations", orgId, "timeLogs", log.id);
            const logDoc = await getDoc(logRef);

            if (logDoc.exists()) {
                const data = logDoc.data();
                setCorrectionHistory(data.corrections || []);
                setHistoryDialogOpen(true);
            }
        } catch (err) {
            console.error("Error fetching correction history:", err.message);
            setError("Failed to load correction history.");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseHistoryDialog = () => {
        setHistoryDialogOpen(false);
        setCorrectionHistory([]);
    };

    const handleTimestampChange = (e) => {
        setNewTimestampString(e.target.value);
    };

    const handleCorrectTimeEntry = async () => {
        if (!selectedLog || !newTimestampString) {
            setError("Invalid data for correction");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Convert the datetime string to Date object
            const newDate = new Date(newTimestampString);

            if (isNaN(newDate.getTime())) {
                throw new Error("Invalid date format");
            }

            const timeLogRef = doc(db, "organizations", orgId, "timeLogs", selectedLog.id);
            const logDoc = await getDoc(timeLogRef);

            if (!logDoc.exists()) {
                throw new Error("Time log not found");
            }

            const logData = logDoc.data();
            const originalTimestamp = logData.timestamp;

            // Get existing corrections or initialize empty array
            const existingCorrections = logData.corrections || [];

            // Create correction entry with ISO string timestamp instead of serverTimestamp
            const correctionEntry = {
                correctedAt: new Date().toISOString(), // Use ISO string instead of serverTimestamp
                correctedBy: "admin", // You might want to pass in the actual admin ID
                originalValue: originalTimestamp,
                newValue: Timestamp.fromDate(newDate)
            };

            // Update the document with the new timestamp and add to corrections history
            await updateDoc(timeLogRef, {
                timestamp: Timestamp.fromDate(newDate),
                corrections: [...existingCorrections, correctionEntry]
            });

            // Refresh time logs
            fetchTimeLogs();

            setSuccess("Time entry corrected successfully");
            handleCloseEditDialog();
        } catch (err) {
            console.error("Error correcting time entry:", err);
            setError(`Failed to correct time entry: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                    <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Time Entry Corrections
                </Typography>
                <Button
                    variant="contained"
                    onClick={fetchTimeLogs}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Employee</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Timestamp</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {timeLogs.map((log) => (
                                <TableRow key={log.id}>
                                    <TableCell>{log.email}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.type === "clockIn" ? "Clock In" : "Clock Out"}
                                            color={log.type === "clockIn" ? "success" : "error"}
                                            size="small"
                                        />
                                        {log.manualEntry && (
                                            <Chip
                                                label="Manual"
                                                size="small"
                                                variant="outlined"
                                                sx={{ ml: 1 }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {log.timestamp?.toDate().toLocaleString()}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleOpenEditDialog(log)}
                                            size="small"
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        {log.corrections && log.corrections.length > 0 && (
                                            <IconButton
                                                color="secondary"
                                                onClick={() => handleOpenHistoryDialog(log)}
                                                size="small"
                                            >
                                                <HistoryIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Edit Time Entry Dialog */}
            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog}>
                <DialogTitle>Edit Time Entry</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Change the timestamp for this {selectedLog?.type === "clockIn" ? "clock in" : "clock out"} entry.
                    </DialogContentText>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            label="New Timestamp"
                            type="datetime-local"
                            value={newTimestampString}
                            onChange={handleTimestampChange}
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog}>Cancel</Button>
                    <Button
                        onClick={handleCorrectTimeEntry}
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Correction History Dialog */}
            <Dialog
                open={historyDialogOpen}
                onClose={handleCloseHistoryDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Correction History</DialogTitle>
                <DialogContent>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date Corrected</TableCell>
                                    <TableCell>Original Value</TableCell>
                                    <TableCell>New Value</TableCell>
                                    <TableCell>Corrected By</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {correctionHistory.map((correction, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {correction.correctedAt instanceof Timestamp
                                                ? correction.correctedAt.toDate().toLocaleString()
                                                : new Date(correction.correctedAt).toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            {correction.originalValue instanceof Timestamp
                                                ? correction.originalValue.toDate().toLocaleString()
                                                : new Date(correction.originalValue).toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            {correction.newValue instanceof Timestamp
                                                ? correction.newValue.toDate().toLocaleString()
                                                : new Date(correction.newValue).toLocaleString()}
                                        </TableCell>
                                        <TableCell>{correction.correctedBy}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseHistoryDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TimeEntryCorrections;